import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { CheckCircle, AlertTriangle, Scale } from "lucide-react";

interface TrialBalanceAccount {
  accountId: number;
  code: string;
  name: string;
  type: string;
  subtype: string | null;
  debit: number;
  credit: number;
}

interface TrialBalanceData {
  asOfDate: string;
  accounts: TrialBalanceAccount[];
  totals: { totalDebit: number; totalCredit: number; isBalanced: boolean };
  uncategorizedCount: number;
}

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const typeLabel: Record<string, string> = {
  asset: "Asset", liability: "Liability", equity: "Equity",
  revenue: "Revenue", income: "Revenue", expense: "Expense",
};

export default function TrialBalancePage() {
  const { activeBusiness } = useBusinesses();
  const [, navigate] = useLocation();
  const businessId = activeBusiness?.id;

  const { data, isLoading } = useQuery<TrialBalanceData>({
    queryKey: ["/api/businesses", businessId, "reports/trial-balance"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/businesses/${businessId}/reports/trial-balance`);
      return res.json();
    },
    enabled: !!businessId,
  });

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scale className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Trial Balance</h1>
              <p className="text-muted-foreground text-sm">
                {data ? `As of ${new Date(data.asOfDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}` : "Loading..."}
              </p>
            </div>
          </div>
          {data && (
            <Badge className={data.totals.isBalanced ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
              {data.totals.isBalanced ? <><CheckCircle className="h-3 w-3 mr-1" /> Balanced</> : <><AlertTriangle className="h-3 w-3 mr-1" /> Imbalanced</>}
            </Badge>
          )}
        </div>

        {data && data.uncategorizedCount > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {data.uncategorizedCount} transactions are uncategorized and excluded from this report.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10" />)}</div>
            ) : !data || data.accounts.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Scale className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No approved transactions to display.</p>
                <p className="text-sm mt-1">Categorize and approve transactions to generate the trial balance.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-20">Code</TableHead>
                    <TableHead>Account Name</TableHead>
                    <TableHead className="w-24">Type</TableHead>
                    <TableHead className="w-32 text-right">Debit</TableHead>
                    <TableHead className="w-32 text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.accounts.map((a) => (
                    <TableRow key={a.accountId} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/reports/general-ledger?account_id=${a.accountId}`)}>
                      <TableCell className="font-mono text-sm">{a.code}</TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{typeLabel[a.type] || a.type}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmt(a.debit)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(a.credit)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow className="bg-muted/50 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right">Totals</TableCell>
                    <TableCell className="text-right font-mono">{fmt(data.totals.totalDebit)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(data.totals.totalCredit)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
